import type { Page } from '@ps/web-core-store';
import { StarsButton } from '@ps/web-shared-components';
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import { PageBody } from '@swf/cms';
import useRequest from '@swf/core/hooks/useRequest';
import type { PageResponse } from '@swf/core/models/cms';
import type { Dispatch } from '@swf/core/store';
import { timeout } from '@swf/core/utils';

import type { PageType } from '../../types/ContentOverlay';
import { pageContents } from '../../utils/contentOverlay';
import HelpModal from '../HelpModal/HelpModal';

import { TXTACL_Close } from './ContentOverlay.messages.json';
import { button, buttonContainer, container } from './ContentOverlay.scss';

interface Props {
  pageType: PageType;
  onClose(): void;
  sectionId?: string;
}

const ContentOverlay: React.FC<Props> = ({ pageType, onClose, sectionId }) => {
  const request = useRequest();
  const [cmsPage, setCmsPage] = useState<Page>();
  const [panelVisible, setPanelVisible] = useState(false);
  const {
    cms: { update }
  } = useDispatch<Dispatch>();

  const pageContentDetails = pageContents.get(pageType);

  useEffect(() => {
    request<PageResponse>({
      service: 'cms.page',
      body: { path: `/embedded/${pageContentDetails?.link}` }
    }).subscribe(({ body }) => {
      const { content, page } = body;
      update({ content });
      setCmsPage(page);
      setPanelVisible(true);
    });
  }, [request, pageType, pageContentDetails?.link, update]);

  const scroll = useCallback(() => {
    if (sectionId) {
      document.getElementById(sectionId)?.scrollIntoView();
    }
  }, [sectionId]);

  const handleClose = useCallback(async () => {
    setPanelVisible(false);
    await timeout(500);
    onClose();
  }, [onClose]);

  return (
    <HelpModal
      visible={panelVisible}
      onMount={scroll}
      onClose={handleClose}
      title={pageContentDetails?.title}
      variant="contentOverlay"
    >
      <>
        <div className={container}>
          {cmsPage && (
            <PageBody
              side={cmsPage.content?.[0].side}
              main={cmsPage.content?.[0].main}
              twoColumns={false}
              darkTheme={cmsPage.theme === 'Dark'}
            />
          )}
        </div>
        <div className={buttonContainer}>
          <StarsButton className={button} onClick={handleClose} translation={TXTACL_Close} />
        </div>
      </>
    </HelpModal>
  );
};

ContentOverlay.displayName = 'ContentOverlay';

export default ContentOverlay;
